import React, { useState } from 'react';
import { Modal, RadioButton } from '../../../../components';
import { useMutate, useAppAndBranchId } from '../../../../hooks';
import { api } from '../../api';
import { useToast, uuid } from '../../../../../../../../helpers/commonFunction';
import style from './add-simple-choice.module.scss';
import AddIcon from '../../../../../../../../assets/svg/solo-chat/circle-plus.svg';
import { TChoice, Choice } from './Choice';
import { sortableOptions } from '../../../../../soloJourney/properties/message/components';
import { ReactSortable } from 'react-sortablejs';
import { ContentMode } from '../../../../types';
import { validateList } from '../../../../utils/validation';
import { errorMessage } from '../../../../utils/error';

type SimpleChoice = {
	title: string;
	orientation: 'V' | 'H';
	choices: { id: string; label: string; _id: string }[];
};

type Props = {
	isOpen: boolean;
	close: () => void;
	mode?: ContentMode;
	initialValue?: SimpleChoice & { id: string };
	refetchContent: () => void;
	onCreate?: (contentId: string) => void;
	isPreview?: boolean;
};

export function AddSimpleChoice(props: Props) {
	const { ErrorToast, WarningToast } = useToast();
	const [appId, branchId] = useAppAndBranchId();
	const {
		isOpen,
		close,
		initialValue,
		refetchContent,
		onCreate,
		isPreview = false,
	} = props;

	// the _id generated column is used to preserve keys between rerenders
	const [state, setState] = useState<SimpleChoice>(() => {
		if (initialValue) {
			return {
				...initialValue,
				choices: initialValue.choices.map((el) => ({ ...el, _id: uuid() })),
			};
		}
		return {
			choices: [],
			title: '',
			orientation: 'V',
		};
	});
	const [mode, setMode] = useState(props.mode ?? 'create');

	const { mutate: createMutate, loading: createLoading } = useMutate(
		api.createContent,
	);
	const { mutate: updateMutate, loading: updateLoading } = useMutate(
		api.updateContent,
	);

	const [error, setError] = useState<{
	 title: string
	 choices: Record<string, string>[]
	 minChoice: string
	}>({
		title: "",
		choices: [],
		minChoice: "",
	})

	function clearErrorChoice(index: number, key: string) {
	  setError(prev => ({
		 ...prev,
		 choices: prev.choices.map((ch, i) => i === index ? {...ch, [key]: ""} : ch)
		}))
	}

	function clearError(key: keyof typeof error) {
	 setError(prev => ({...prev, [key]: ""}))
	}

	function addChoice() {
		setState((prev) => ({
			...prev,
			choices: [
				...prev.choices,
				{
					_id: uuid(),
					id: '',
					label: '',
				},
			],
		}));
	}

	function updateChoice(key: keyof TChoice, value: any, index: number) {
		setState((prev) => ({
			...prev,
			choices: prev.choices.map((el, i) =>
				i === index ? { ...el, [key]: value } : el,
			),
		}));
	}

	function removeChoice(index: number) {
		setState((prev) => ({
			...prev,
			choices: prev.choices.filter((_, i) => i !== index),
		}));
	}

	function validateSimpleChoice() {
		const choicesValidation = state.choices.map((choice, i) => {
			const rulesFn = (prefix: string) => {
				return [
					{
						message: `${prefix} can't be empty`,
						fn: (value: string) => value.trim().length <= 0,
					},
				];
			};
			return validateList([
			 { name: 'id', value: choice.id, rules: [
			   ...rulesFn('Id'),
				 {
				   message: 'Id already exists',
					 fn: (value: string) => {
						 const previousChoices = state.choices.slice(0, i).map(el => el.id.trim())
						 return previousChoices.includes(value.trim())
					 }
				 }
			 ] },
			 { name: 'label', value: choice.label, rules: rulesFn('Label') }
			]);
		}).map((item, i) => ({ ...item, _id: state.choices[i]._id }))

		const error = {
			choices: choicesValidation,
			title: !state.title.trim().length ? "Title can't be empty" : "",
			minChoice: !state.choices.length ? "Atleast one option should be included" : ""
		}

		if(error.minChoice) {
			WarningToast(error.minChoice)
		}

		let hasError = false

		if(error.title || error.minChoice) {
		  hasError = true
		}

		let choicesError = false
		error.choices.forEach(({ _id, ...rest }) => {
		  if(Object.keys(rest).length !== 0) {
				hasError = true
				choicesError = true
			}
		})

		if(choicesError) {
		  WarningToast("Choices contains empty or incorrect values")
		}

		setError(error)
		return !hasError;
	}

	return (
		<Modal
			hideFooter={isPreview}
			loading={createLoading || updateLoading}
			isOpen={isOpen}
			styles={{ '--modal-footer-mt': '0px' }}
			title={
				mode === 'edit'
					? 'Update Simple Choice'
					: mode === 'create'
					? 'Add Simple Choice'
					: 'View Simple Choice'
			}
			okText={
				mode === 'view' ? 'Edit' : mode === 'create' ? 'Submit' : 'Update'
			}
			onCancel={() => {
				setState({
					title: '',
					choices: [],
					orientation: 'V',
				});
				close();
			}}
			onOk={async () => {
				if (mode !== 'view' && !validateSimpleChoice()) {
					return;
				}
				const payload = {
					branchId,
					contentType: 'simpleChoice',
					contentValue: {
						title: state.title.trim(),
						style: { orientation: state.orientation },
						choices: state.choices.map(({ _id, ...rest }) => rest),
					},
				};

				try {
					if (mode === 'edit') {
						await updateMutate(appId, initialValue?.id, payload);
					} else if (mode === 'create') {
						const res = await createMutate(appId, payload);
						if (onCreate) {
							onCreate(res.contentId);
						}
					} else {
						setMode('edit');
						return;
					}
					refetchContent();
					close();
				} catch (err) {
					ErrorToast(errorMessage(mode, 'simple choice', err.code));
				}
			}}
		>
			<div className={style.addSimpleChoice}>
				<div className={style.titleWrapper}>
					<label>Title</label>
					<input
						autoFocus
						disabled={mode === 'view'}
						placeholder="Enter title"
						value={state.title}
						onChange={(e) => {
							clearError("title")
							const value = e.currentTarget.value;
							setState((prev) => ({ ...prev, title: value }));
						}}
					/>
					<span className={style.error}>{error.title}</span>
				</div>

				<div className={style.choiceStyle}>
					<p style={{ color: '#767B83', marginBottom: 4 }}>
						Choice Orientation
					</p>
					<RadioButton
						disabled={mode === 'view'}
						options={options}
						selected={state.orientation}
						onSelect={(value: any) =>
							setState((prev) => ({ ...prev, orientation: value }))
						}
					/>
				</div>

				<div className={style.addChoices}>
					{(mode !== 'view' || state.choices.length > 0) && <p>Choices</p>}
					{mode !== 'view' && (
						<>
							<div className={style.line} />
							<button className={style.add} onClick={addChoice}>
								<img src={AddIcon} alt="add" />
								<span>Add Choices</span>
							</button>
						</>
					)}
				</div>

				<p className={style.dim}>At least one option should be included.</p>

				{state.choices.length > 0 && (
					<div className={style.choicesHeader}>
						<p>Id</p>
						<p>Label</p>
					</div>
				)}

				<ReactSortable
					className={style.choices}
					list={state.choices}
					setList={(data) =>
						setState((prev) => ({
							...prev,
							choices: data.map((el: any) => ({
								id: el.id,
								label: el.label,
								_id: el._id,
							})),
						}))
					}
					{...sortableOptions}
				>
					{state.choices.map((el, i) => {
						return (
							<Choice
								clearError={clearErrorChoice}
							  error={error.choices.find(choice => choice._id === el._id)}
								mode={mode}
								key={el._id}
								index={i}
								choice={el}
								updateChoice={updateChoice}
								removeChoice={removeChoice}
							/>
						);
					})}
				</ReactSortable>
			</div>
		</Modal>
	);
}

const options = [
	{ label: 'Vertical', value: 'V' },
	{ label: 'Horizontal', value: 'H' },
];
